import {ChangeEvent, useMemo, useState} from 'react';

import {FieldArray, Form, Formik} from 'formik';
import {useTranslation} from 'react-i18next';

import {Grid, Typography} from '@mui/material';

import {
  surveySectionDescriptionStyles,
  surveySectionTitleStyles,
} from '../surveyQuestionnaireTypography';

import BenefitsQuestions from './BenefitsQuestions';
import {EmpionButton} from '../../../../../../../components/EmpionButton/EmpionButton';
import {BenefitsRequestBody} from '../../../../../../../lib/api/RequestBodies/features/company/Questionnaires/QuestionnaireRequestBodies';
import {BenefitsOptions} from '../../../types/SurveyQuestionnaireTypes';

interface Props {
  existingBenefits: BenefitsOptions[];
  desiredBenefits: BenefitsOptions[];
  onSubmit: (answers: BenefitsRequestBody) => void;
}

enum BenefitSection {
  Existing,
  Desired,
}

export default function QuestionnaireBenefits({existingBenefits, desiredBenefits, onSubmit}: Props) {
  const {t} = useTranslation();

  const [desiredBenefitsIds, setDesiredBenefitsIds] = useState<string[]>([]);
  const [benefitsIds, setBenefitsIds] = useState<string[]>([]);
  const [clearCheckboxes, setClearCheckboxes] = useState<boolean>(false);
  const [step, setStep] = useState<BenefitSection>(BenefitSection.Existing);

  const displayedSection = useMemo(() => {
    return {
      [BenefitSection.Existing]: existingBenefits,
      [BenefitSection.Desired]: desiredBenefits,
    };
  }, [existingBenefits, desiredBenefits]);

  const handleChangeEvent = (event: ChangeEvent<HTMLInputElement>) => {
    const {value} = event.target;
    if (step === BenefitSection.Existing) {
      if (benefitsIds.includes(value)) {
        setBenefitsIds(benefitsIds.filter((benefit) => benefit !== value));
      } else {
        setBenefitsIds([...benefitsIds, value]);
      }
    } else {
      if (desiredBenefitsIds.includes(value)) {
        setDesiredBenefitsIds(desiredBenefitsIds.filter((benefit) => benefit !== value));
      } else {
        setDesiredBenefitsIds([...desiredBenefitsIds, value]);
      }
    }
  };

  const handleSubmit = async () => {
    if (step === BenefitSection.Existing) {
      setClearCheckboxes(true);
      setStep(BenefitSection.Desired);
      return;
    }
    const payload: BenefitsRequestBody = {
      answers: {
        existingBenefits: benefitsIds,
        desiredBenefits: desiredBenefitsIds,
      },
    };

    onSubmit(payload);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '2rem',
          flexDirection: 'column',
        }}
      >
        <Typography variant='h2' gutterBottom align='center' sx={surveySectionTitleStyles}>
          {step === BenefitSection.Existing
            ? t('companySurveys.public.benefits.benefitsQuestions')
            : t('companySurveys.public.benefits.addBenefitsTitle')}
        </Typography>
        <Typography variant='body1' align='center' gutterBottom sx={surveySectionDescriptionStyles}>
          {step === BenefitSection.Existing
            ? t('companySurveys.public.benefits.benefitsDescription')
            : t('companySurveys.public.benefits.addBenefitsDescription')}
        </Typography>
        {step === BenefitSection.Existing && (
          <Typography
            variant='body2'
            align='center'
            sx={{
              fontStyle: 'italic',
            }}
          >
            {t('companySurveys.public.benefits.selectBenefits')}
          </Typography>
        )}
      </Grid>
      <Formik initialValues={{}} onSubmit={handleSubmit}>
        {() => (
          <Form>
            <FieldArray
              name='benefits'
              render={() => (
                <Grid
                  container
                  sx={{
                    justifyContent: 'center',
                  }}
                >
                  {displayedSection[step].map((benefitCategory, index) => {
                    return (
                      <Grid
                        key={index}
                        container
                        item
                        xs={12}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant='h2'
                          sx={{
                            textTransform: 'capitalize',
                            marginTop: '2rem',
                          }}
                        >
                          {benefitCategory.category}
                        </Typography>
                        <BenefitsQuestions
                          benefits={benefitCategory.benefits}
                          handleChange={handleChangeEvent}
                          clearCheckboxes={clearCheckboxes}
                        />
                      </Grid>
                    );
                  })}
                  <Grid
                    item
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: {xs: '1rem', md: '2rem'},
                    }}
                  >
                    {step === BenefitSection.Existing ? (
                      <EmpionButton
                        disabled={benefitsIds.length === 0}
                        sx={{
                          mt: 3,
                        }}
                        type='submit'
                      >
                        {t('shared.formSubmit.submit')}
                      </EmpionButton>
                    ) : (
                      <EmpionButton
                        sx={{
                          mt: 3,
                        }}
                        type='submit'
                      >
                        {desiredBenefitsIds.length !== 0 ? t('shared.formSubmit.submit') : t('shared.formButtons.next')}
                      </EmpionButton>
                    )}
                  </Grid>
                </Grid>
              )}
            />
          </Form>
        )}
      </Formik>
    </Grid>
  );
}
